import { useEffect, useMemo, useState } from "react";
import { detectInstitutionalPatterns } from "../lib/patterns";
import { verdictOf, VERDICT_STYLE } from "../lib/patternVerdicts";

// Watchlist: alle aksjene dine i én tabell med det som betyr noe for en beslutning:
// trend, styrke mot markedet, avstand til topp, opsjonspris (IV-rang), neste rapport og testede signaler.

const C = { bg: "#060f1c", bg2: "#0a1628", border: "#1e293b", dim: "#475569", text: "#94a3b8", bright: "#e2e8f0",
  green: "#10b981", yellow: "#f59e0b", red: "#f43f5e", blue: "#3b82f6" };
const fmtP = (v, d = 1) => (v == null ? "–" : `${v >= 0 ? "+" : ""}${v.toFixed(d)}%`);
const pc = v => (v == null ? C.dim : v >= 0 ? C.green : C.red);
const TREND = { opp: ["↗ opp", C.green], ned: ["↘ ned", C.red], blandet: ["→ blandet", C.yellow] };

async function getPerf(tickers) {
  const out = {};
  for (let i = 0; i < tickers.length; i += 40) {
    const r = await fetch(`/perf?t=${encodeURIComponent(tickers.slice(i, i + 40).join(","))}`);
    if (r.ok) Object.assign(out, await r.json());
  }
  return out;
}

export default function Watchlist({ stocks, favorites, setFavorites, liveData, techData, fetchQuote, catColors, isMobile, onOpen }) {
  const [perf, setPerf] = useState(null);
  const [ivr, setIvr] = useState({});
  const [cat, setCat] = useState("Alle");
  const [onlyFav, setOnlyFav] = useState(false);
  const [onlyUp, setOnlyUp] = useState(false);
  const [sort, setSort] = useState({ key: "rs1", dir: -1 });
  const [newFav, setNewFav] = useState("");
  const local = window.location.hostname === "localhost";

  // Watchlist + favoritter som ikke står i noen kategori
  const rowsBase = useMemo(() => {
    const list = Object.entries(stocks).flatMap(([c, l]) => l.map(s => ({ ...s, cat: c })));
    const known = new Set(list.map(s => s.ticker));
    favorites.filter(t => !known.has(t)).forEach(t => list.push({ ticker: t, name: "", cat: "Favoritter" }));
    return list;
  }, [stocks, favorites]);
  const key = rowsBase.map(r => r.ticker).join(",");

  useEffect(() => {
    if (local) return;
    const t = [...new Set(["SPY", ...key.split(",").filter(Boolean)])];
    getPerf(t).then(setPerf).catch(() => setPerf(p => p || {}));
    fetch(`/api/ivrank?t=${encodeURIComponent(key)}`).then(r => r.json()).then(d => setIvr(d?.error ? {} : d)).catch(() => {});
  }, [key, local]);

  const addFav = () => {
    const t = newFav.trim().toUpperCase();
    if (!t) return;
    if (!favorites.includes(t)) { setFavorites(prev => [...prev, t]); if (!liveData[t]) fetchQuote(t); }
    setNewFav("");
  };
  const toggleFav = t => setFavorites(prev => (prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]));

  const today = new Date().toLocaleDateString("en-CA");
  const rows = useMemo(() => rowsBase.map(s => {
    const p = perf?.[s.ticker], d = liveData[s.ticker], spy = perf?.SPY;
    const trend = p?.ma50 && p?.ma200 ? (p.price > p.ma50 && p.ma50 > p.ma200 ? "opp" : p.price < p.ma50 && p.ma50 < p.ma200 ? "ned" : "blandet") : null;
    // ferske mønstre (siste 5 handelsdager) med backtest-dom
    let pats = [];
    const td = techData[s.ticker];
    if (td?.closes?.length && td.timestamps?.length) {
      const days = new Set(td.timestamps.slice(-5).map(ts => new Date(ts * 1000).toISOString().slice(0, 10)));
      const by = new Map();
      detectInstitutionalPatterns(td.closes, td.opens || [], td.highs || [], td.lows || [], td.volumes || [], td.timestamps)
        .filter(x => days.has(x.date)).forEach(x => by.set(x.type, x));
      pats = [...by.values()].map(x => ({ ...x, vd: verdictOf(x.type) }))
        .filter(x => x.vd.v === "pålitelig" || x.vd.v === "lovende");
    }
    const earn = d?.nextEarnings && d.nextEarnings >= today ? d.nextEarnings : null;
    return {
      ...s, d, p, trend, pats, earn,
      price: d?.price ?? p?.price, day: d?.change ?? p?.d1,
      rs1: p?.m1 != null && spy?.m1 != null ? p.m1 - spy.m1 : null,
      rs3: p?.m3 != null && spy?.m3 != null ? p.m3 - spy.m3 : null,
      fromHigh: p?.fromHigh ?? null, iv: ivr[s.ticker],
      earnDays: earn ? Math.round((new Date(earn + "T12:00:00") - Date.now()) / 86400000) : null,
      fav: favorites.includes(s.ticker),
    };
  }), [rowsBase, perf, liveData, techData, ivr, favorites, today]);

  const cats = ["Alle", ...new Set(rowsBase.map(r => r.cat))];
  const shown = rows
    .filter(r => (cat === "Alle" || r.cat === cat) && (!onlyFav || r.fav) && (!onlyUp || r.trend === "opp"))
    .sort((a, b) => {
      const va = sort.key === "ticker" ? a.ticker : sort.key === "iv" ? a.iv?.rank : a[sort.key];
      const vb = sort.key === "ticker" ? b.ticker : sort.key === "iv" ? b.iv?.rank : b[sort.key];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return (va < vb ? -1 : va > vb ? 1 : 0) * sort.dir;
    });

  const th = (k, label, title) => (
    <th key={k} title={title} onClick={() => setSort(s => ({ key: k, dir: s.key === k ? -s.dir : -1 }))}
      style={{ padding: "8px 8px", textAlign: k === "ticker" ? "left" : "right", fontSize: 9, letterSpacing: 1, cursor: "pointer", whiteSpace: "nowrap",
        color: sort.key === k ? "#93c5fd" : C.dim, fontWeight: sort.key === k ? 700 : 400 }}>
      {label}{sort.key === k ? (sort.dir < 0 ? " ↓" : " ↑") : ""}
    </th>
  );
  const chip = (on, set, label) => (
    <button onClick={() => set(v => !v)}
      style={{ padding: "4px 10px", fontSize: 10, fontFamily: "inherit", cursor: "pointer", borderRadius: 4, border: "1px solid",
        borderColor: on ? C.blue : C.border, background: on ? C.blue + "20" : "transparent", color: on ? "#93c5fd" : C.dim }}>{label}</button>
  );
  const td_ = { padding: "7px 8px", textAlign: "right", fontSize: 11, whiteSpace: "nowrap" };

  return (
    <div style={{ maxWidth: 1200 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#f8fafc", letterSpacing: 1 }}>📋 WATCHLIST</div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>Trend, styrke mot S&P 500, opsjonspris og testede signaler for alle aksjene dine. Klikk en rad for Deep Dive.</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <input value={newFav} onChange={e => setNewFav(e.target.value.toUpperCase())} onKeyDown={e => e.key === "Enter" && addFav()} placeholder="Ny favoritt (ticker)"
            style={{ padding: "6px 10px", background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 4, color: C.bright, fontSize: 12, fontFamily: "inherit", width: 140 }} />
          <button onClick={addFav} style={{ padding: "6px 12px", background: "#1e3a5f", border: `1px solid ${C.blue}`, borderRadius: 4, color: "#93c5fd", cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}>★ Legg til</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
        {cats.map(c => (
          <button key={c} onClick={() => setCat(c)}
            style={{ padding: "4px 10px", fontSize: 10, fontFamily: "inherit", cursor: "pointer", borderRadius: 4, border: "1px solid",
              borderColor: cat === c ? (catColors[c] || C.yellow) : C.border, background: cat === c ? (catColors[c] || C.yellow) + "20" : "transparent",
              color: cat === c ? (catColors[c] || C.yellow) : C.dim }}>{c}</button>
        ))}
        <span style={{ width: 10 }} />
        {chip(onlyFav, setOnlyFav, "★ Bare favoritter")}
        {chip(onlyUp, setOnlyUp, "↗ Bare opptrend")}
      </div>

      {local && <div style={{ fontSize: 11, color: C.dim, marginBottom: 8 }}>Trend, styrke og IV hentes via Cloudflare og vises bare på aiaksjer.pages.dev.</div>}

      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}`, background: C.bg2 }}>
              <th style={{ width: 26 }} />
              {th("ticker", "AKSJE")}
              {th("price", "PRIS")}
              {th("day", "DAG")}
              {th("trend", "TREND", "Kurs mot 50- og 200-dagers snitt")}
              {th("rs1", "VS S&P 1M", "Utvikling siste måned minus S&P 500")}
              {th("rs3", "VS S&P 3M", "Utvikling siste 3 måneder minus S&P 500")}
              {th("fromHigh", "FRA TOPP", "Avstand til høyeste kurs siste år")}
              {th("iv", "IV-RANG", "Hvor dyre opsjonene er mot siste år: lav = billige, høy = dyre")}
              {th("earnDays", "RAPPORT", "Neste kvartalsrapport fra Nasdaqs kalender. før = før børsåpning, etter = etter stengning")}
              <th style={{ padding: "8px", textAlign: "left", fontSize: 9, letterSpacing: 1, color: C.dim }}>TESTEDE SIGNALER</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(r => {
              const col = catColors[r.cat] || C.text;
              const pre = r.d?.preMarketPrice != null ? ["PRE", r.d.preMarket] : r.d?.postMarketPrice != null ? ["ETTER", r.d.postMarket] : null;
              return (
                <tr key={r.ticker} onClick={() => onOpen(r.ticker)} style={{ borderBottom: `1px solid ${C.bg2}`, cursor: "pointer" }}
                  onMouseEnter={e => (e.currentTarget.style.background = C.bg2)} onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
                  <td style={{ padding: "7px 4px 7px 10px" }}>
                    <span onClick={e => { e.stopPropagation(); toggleFav(r.ticker); }} title={r.fav ? "Fjern favoritt" : "Gjør til favoritt"}
                      style={{ cursor: "pointer", color: r.fav ? C.yellow : "#334155", fontSize: 13 }}>{r.fav ? "★" : "☆"}</span>
                  </td>
                  <td style={{ padding: "7px 8px", whiteSpace: "nowrap" }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: col }}>{r.ticker}</span>
                    {!isMobile && <span style={{ fontSize: 10, color: C.dim, marginLeft: 6 }}>{r.name}</span>}
                  </td>
                  <td style={{ ...td_, color: C.bright, fontWeight: 700 }}>{r.price != null ? `$${r.price < 10 ? r.price.toFixed(2) : r.price.toFixed(1)}` : "…"}</td>
                  <td style={{ ...td_, color: pc(r.day) }}>
                    {fmtP(r.day, 2)}
                    {pre && pre[1] != null && <div style={{ fontSize: 9, color: pc(pre[1]) }}>{pre[0]} {fmtP(pre[1], 1)}</div>}
                  </td>
                  <td style={{ ...td_, color: r.trend ? TREND[r.trend][1] : C.dim }}>{r.trend ? TREND[r.trend][0] : "–"}</td>
                  <td style={{ ...td_, color: pc(r.rs1), fontWeight: 700 }}>{fmtP(r.rs1)}</td>
                  <td style={{ ...td_, color: pc(r.rs3) }}>{fmtP(r.rs3)}</td>
                  <td style={{ ...td_, color: r.fromHigh != null && r.fromHigh > -5 ? C.green : C.text }}>{fmtP(r.fromHigh, 0)}</td>
                  <td style={{ ...td_ }} title={r.iv ? `IV30 ${(r.iv.iv * 100).toFixed(0)} % · ${r.iv.n} dager logget` : "Ikke logget ennå"}>
                    {r.iv?.rank != null
                      ? <span style={{ color: r.iv.rank < 30 ? C.green : r.iv.rank > 70 ? C.red : C.yellow, fontWeight: 700 }}>{r.iv.rank}</span>
                      : r.iv ? <span style={{ color: C.dim }}>{(r.iv.iv * 100).toFixed(0)}% <span style={{ fontSize: 9 }}>({r.iv.n}/20)</span></span> : <span style={{ color: "#334155" }}>–</span>}
                  </td>
                  <td style={{ ...td_, color: r.earnDays != null && r.earnDays <= 14 ? C.yellow : C.dim }}>
                    {r.earn ? `${new Date(r.earn + "T12:00:00").toLocaleDateString("nb-NO", { day: "numeric", month: "short" })}${r.d?.nextEarningsTime === "før" ? " før" : r.d?.nextEarningsTime === "etter" ? " etter" : ""} (${r.earnDays}d)` : "–"}
                  </td>
                  <td style={{ padding: "7px 8px" }}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {r.pats.map(p => (
                        <span key={p.type} title={p.vd.note} style={{ fontSize: 10, padding: "1px 6px", borderRadius: 3, border: `1px solid ${VERDICT_STYLE[p.vd.v].color}60`,
                          color: p.vd.bias === "bearish" ? C.red : VERDICT_STYLE[p.vd.v].color, fontWeight: 700 }}>
                          {p.vd.bias === "bearish" ? "⚠ " : "▲ "}{p.vd.label}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 10, color: "#334155", marginTop: 8, lineHeight: 1.6 }}>
        «Vs S&P» = aksjens utvikling minus S&P 500 i samme periode: positiv betyr at den er sterkere enn markedet. Testede signaler viser bare mønstre
        som backtesten fant en fordel i (Bull Trap, Shooting Star, RSI-divergens) de siste 5 handelsdagene. IV-rang vises etter 20 handelsdager med logging.
      </div>
    </div>
  );
}
